/**
 * Sticky Add-to-Cart Bar
 * Shows a fixed bottom bar when the main ATC button scrolls out of view.
 * Syncs variant selection via the theme's PUB_SUB_EVENTS.variantChange system.
 */
class StickyATC extends HTMLElement {
  connectedCallback() {
    const targetSelector = this.dataset.targetButton;
    if (!targetSelector) return;

    this.targetButton = document.querySelector(targetSelector);
    if (!this.targetButton) return;

    this._setupObserver();
    this._trackViewportChanges();
    this._bindVariantSync();
    this._bindPurchasePrice();
    this._bindSubmit();
  }

  // Read the same variant and selling-plan allocation submitted by the main
  // form. Never infer a discount from the plan's marketing percentage.
  _bindPurchasePrice() {
    this._mainForm = this.targetButton.closest('form');
    if (!this._mainForm || !this.stickyPrice) return;
    const picker = document.querySelector(`variant-selects[data-section="${this.dataset.sectionId}"]`);
    const source = picker?.querySelector('script[type="application/json"]')?.textContent;
    const product = this._mainForm.querySelector('.sealsubs-target-element')?.dataset.product;
    try {
      this._purchaseVariants = source ? JSON.parse(source) : JSON.parse(product || '{}').variants || [];
    } catch {
      this._purchaseVariants = [];
    }
    this._priceFrame = null;
    this._onPurchaseChange = () => {
      if (this._priceFrame !== null) return;
      this._priceFrame = window.requestAnimationFrame(() => {
        this._priceFrame = null;
        this._updatePurchasePrice();
      });
    };
    this._mainForm.addEventListener('change', this._onPurchaseChange);
    // Apps can install the hidden plan field late or update its value without
    // emitting change. Observe only the purchase form, never the floating bar.
    this._purchaseObserver = new MutationObserver(this._onPurchaseChange);
    this._purchaseObserver.observe(this._mainForm, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'checked'],
    });
    this._onPurchaseChange();
  }

  _updatePurchasePrice() {
    if (!this.stickyPrice) return;
    const variantId = this._mainForm?.querySelector('[name="id"]')?.value;
    const variant = variantId
      ? this._purchaseVariants?.find((v) => String(v.id) === String(variantId))
      : this._currentVariant;
    if (!variant) {
      if (this._purchaseVariants?.length || this._currentVariant === null) {
        this.stickyPrice.hidden = true;
        if (this.stickyMode) this.stickyMode.hidden = true;
      }
      return;
    }
    const planInput = this._mainForm?.querySelector('[name="selling_plan"]');
    const planId = planInput && !planInput.disabled ? planInput.value : '';
    const allocation = planId ? variant.selling_plan_allocations?.find(
      (a) => String(a.selling_plan_id) === String(planId)
    ) : null;
    const amount = planId ? allocation?.price : variant.price;
    // A selected plan without an allocation has no verified price. Avoid
    // misrepresenting the ordinary price while the app refreshes its state.
    this.stickyPrice.hidden = !Number.isFinite(amount) || amount < 0;
    if (this.stickyMode) {
      this.stickyMode.hidden = this.stickyPrice.hidden;
      this.stickyMode.textContent = planId ? 'Subscription' : 'One-time';
    }
    if (this.stickyPrice.hidden) return;
    this.stickyPrice.textContent = typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function'
      ? Shopify.formatMoney(amount)
      : (amount / 100).toLocaleString('en-US', {
          style: 'currency', currency: window.Shopify?.currency?.active || 'USD',
        });
  }

  // A direct jump can leave the button non-intersecting both before and
  // after scrolling, so IntersectionObserver alone misses that movement.
  _trackViewportChanges() {
    this._visibilityFrame = null;
    this._onViewportChange = () => {
      if (this._visibilityFrame !== null) return;
      this._visibilityFrame = window.requestAnimationFrame(() => {
        this._visibilityFrame = null;
        this.classList.toggle('is-visible', this.targetButton.getBoundingClientRect().bottom <= 0);
        this._updateBarOffset();
      });
    };
    window.addEventListener('scroll', this._onViewportChange, { passive: true });
    window.addEventListener('resize', this._onViewportChange);
    this._barResizeObserver = new ResizeObserver(() => this._updateBarOffset());
    this._barResizeObserver.observe(this);
    this._onViewportChange();
  }

  _updateBarOffset() {
    const height = this.classList.contains('is-visible') ? this.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--cn-sticky-height', `${height}px`);
  }

  /**
   * IntersectionObserver on the main ATC button.
   * When the button is NOT intersecting (scrolled out), show the sticky bar.
   */
  _setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const hasScrolledPast = entry.boundingClientRect.bottom <= 0;
          this.classList.toggle('is-visible', !entry.isIntersecting && hasScrolledPast);
          this._onViewportChange?.();
        });
      },
      { threshold: 0 }
    );
    this.observer.observe(this.targetButton);
  }

  /**
   * Listen for variant changes via the theme's pub/sub system
   * and sync the sticky bar's variant select and button state.
   */
  _bindVariantSync() {
    this.stickySelect = this.querySelector('[data-sticky-variant]');
    this.stickySubmit = this.querySelector('[data-sticky-submit]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');
    this.stickyMode = this.querySelector('[data-sticky-mode]');

    // Subscribe to theme variant change events
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this._onVariantChange.bind(this)
      );
    }

    // When user changes the sticky bar's variant select, sync back to main form
    if (this.stickySelect) {
      this.stickySelect.addEventListener('change', (e) => {
        const variantId = e.target.value;
        this._syncMainFormVariant(variantId);
      });
    }
  }

  /**
   * Handle variant change events from the main product form.
   * Update sticky bar select, price, and button state.
   */
  _onVariantChange(event) {
    const { variant, sectionId } = event.data;

    // Only respond to events for our section
    const ourSection = this.dataset.sectionId;
    if (ourSection && sectionId !== ourSection) return;

    if (this.stickySelect) {
      this.stickySelect.value = variant ? variant.id : '';
    }

    this._currentVariant = variant;
    if (this._onPurchaseChange) this._onPurchaseChange();
    else this._updatePurchasePrice();

    if (this.stickySubmit) {
      var span = this.stickySubmit.querySelector('span');
      if (variant && variant.available) {
        this.stickySubmit.removeAttribute('disabled');
        if (span) { span.textContent = window.variantStrings?.addToCart || 'Add to cart'; }
      } else {
        this.stickySubmit.setAttribute('disabled', 'disabled');
        if (span) { span.textContent = window.variantStrings?.soldOut || 'Sold out'; }
      }
    }
  }

  /**
   * Sync the selected variant back to the main product form's
   * variant-selects element by finding the matching option and dispatching
   * a change event.
   */
  _syncMainFormVariant(variantId) {
    const sectionId = this.dataset.sectionId;
    const mainSelect = document.querySelector(
      `#variant-selects-${sectionId} select, variant-selects[data-section="${sectionId}"] select`
    );
    // For themes that use fieldset radios (pill/swatch pickers) we find the
    // hidden input that matches, or fallback to dispatching on the variant-selects element.
    const variantSelects = document.querySelector(
      `variant-selects[data-section="${sectionId}"]`
    );

    // Find the variant data to get option values
    if (variantSelects) {
      const variantData = JSON.parse(
        variantSelects.querySelector('script[type="application/json"]')?.textContent || '[]'
      );
      const variant = variantData.find((v) => String(v.id) === String(variantId));

      if (variant) {
        // Update each option's input/select in the main form
        variant.options.forEach((optionValue, index) => {
          const select = variantSelects.querySelector(
            `select[name="options[${CSS.escape(variant.options[index])}]"], select:nth-of-type(${index + 1})`
          );
          const input = variantSelects.querySelector(
            `input[value="${CSS.escape(optionValue)}"]`
          );

          if (select) {
            select.value = optionValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (input) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }
  }

  /**
   * Clicking the sticky ATC button triggers the main form's submit button.
   */
  _bindSubmit() {
    const stickyBtn = this.querySelector('[data-sticky-submit]');
    if (!stickyBtn) return;

    stickyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.targetButton) {
        this.targetButton.click();
      }
    });
  }

  disconnectedCallback() {
    this._mainForm?.removeEventListener('change', this._onPurchaseChange);
    this._purchaseObserver?.disconnect();
    if (this._priceFrame != null) {
      window.cancelAnimationFrame(this._priceFrame);
      this._priceFrame = null;
    }
    window.removeEventListener('scroll', this._onViewportChange);
    window.removeEventListener('resize', this._onViewportChange);
    if (this._visibilityFrame != null) {
      window.cancelAnimationFrame(this._visibilityFrame);
      this._visibilityFrame = null;
    }
    this._barResizeObserver?.disconnect();
    document.documentElement.style.setProperty('--cn-sticky-height', '0px');
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this._variantChangeUnsubscriber) {
      this._variantChangeUnsubscriber();
    }
  }
}

customElements.define('sticky-atc', StickyATC);
